
import React, { useState } from 'react';
import { motion } from "framer-motion";
import { logos } from "@/data/trustLogos";
import { testimonials } from "@/data/testimonials";
import { caseStudies } from "@/data/caseStudies";
import { LazyImage } from "@/components/ui/lazy-image";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Quote, FileText, ArrowRight, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";

const TrustLogosSection = () => {
  const [selectedTestimonial, setSelectedTestimonial] = useState<typeof testimonials[0] | null>(null);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<typeof caseStudies[0] | null>(null);
  
  // Split logos into two rows - first row gets one more
  const midpoint = Math.floor(logos.length / 2) + 1;
  const firstRow = logos.slice(0, midpoint);
  const secondRow = logos.slice(midpoint);

  // Helper function to check if logo has testimonial
  const getTestimonialForLogo = (logoName: string) => {
    return testimonials.find(t => t.companyName === logoName);
  };

  // Helper function to check if logo has case study
  const getCaseStudyForLogo = (logoName: string) => {
    return caseStudies.find(c => c.companyName === logoName);
  };

  const LogoWithPill = ({ logo, rowKey, index }: { logo: typeof logos[0], rowKey: string, index: number }) => {
    const testimonial = getTestimonialForLogo(logo.name);
    const caseStudy = getCaseStudyForLogo(logo.name);
    
    return (
      <div
        key={`${rowKey}-${logo.name}-${index}`}
        className="flex flex-col items-center gap-2"
      >
        <div className="w-[90px] h-[36px] lg:w-[100px] lg:h-[40px] overflow-hidden flex items-center justify-center">
          <LazyImage
            src={logo.logo}
            alt={`${logo.name} logo`}
            containerClassName="w-full h-full flex items-center justify-center"
            className="max-w-full max-h-full object-contain opacity-50 grayscale hover:grayscale-0 hover:opacity-90 transition-all duration-300 mix-blend-multiply"
          />
        </div>
        {testimonial && (
          <button
            onClick={() => setSelectedTestimonial(testimonial)}
            className="flex items-center gap-0.5 px-1.5 py-0.5 bg-muted/50 hover:bg-muted rounded-full text-[10px] font-medium text-muted-foreground transition-all duration-200 border border-border/50 hover:border-border hover:scale-105"
          >
            <Quote className="w-2.5 h-2.5" />
            <span>Quote</span>
          </button>
        )}
        {caseStudy && (
          <button
            onClick={() => setSelectedCaseStudy(caseStudy)}
            className="flex items-center gap-0.5 px-1.5 py-0.5 bg-muted/50 hover:bg-muted rounded-full text-[10px] font-medium text-muted-foreground transition-all duration-200 border border-border/50 hover:border-border hover:scale-105"
          >
            <FileText className="w-2.5 h-2.5" />
            <span>Case Study</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="relative w-full mx-auto mb-14 2xl:mb-20">
        {/* First Row */}
        <div className="flex items-center justify-center gap-8 lg:gap-10 mb-8 flex-wrap">
          {firstRow.map((logo, index) => (
            <LogoWithPill key={`row1-${index}`} logo={logo} rowKey="row1" index={index} />
          ))}
        </div>
        
        {/* Second Row */}
        <div className="flex items-center justify-center gap-8 lg:gap-10 flex-wrap">
          {secondRow.map((logo, index) => (
            <LogoWithPill key={`row2-${index}`} logo={logo} rowKey="row2" index={index} />
          ))}
        </div>
      </div>

      {/* Testimonial Dialog */}
      <Dialog open={!!selectedTestimonial} onOpenChange={(open) => !open && setSelectedTestimonial(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="sr-only">Customer Testimonial</DialogTitle>
            <DialogDescription className="sr-only">
              Read what our customers say about Assembly
            </DialogDescription>
          </DialogHeader>
          {selectedTestimonial && (
            <div className="space-y-6">
              {/* Company Logo */}
              <div className="flex justify-center py-4">
                <img
                  src={selectedTestimonial.logo}
                  alt={`${selectedTestimonial.companyName} logo`}
                  className="max-w-[150px] max-h-[60px] object-contain"
                />
              </div>

              {/* Quote */}
              <div className="relative">
                <Quote className="absolute -top-2 -left-2 w-8 h-8 text-primary/20" />
                <blockquote className="text-foreground/90 text-lg leading-relaxed pl-8">
                  {selectedTestimonial.quote}
                </blockquote>
              </div>

              {/* Author Info */}
              <div className="flex items-center gap-4 pt-4 border-t border-border">
                <img
                  src={selectedTestimonial.avatar}
                  alt={selectedTestimonial.author}
                  className="w-16 h-16 rounded-full object-cover ring-2 ring-primary/20"
                />
                <div>
                  <h3 className="text-lg font-bold text-foreground">{selectedTestimonial.author}</h3>
                  <p className="text-sm text-muted-foreground">{selectedTestimonial.title}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Case Study Dialog */}
      <Dialog open={!!selectedCaseStudy} onOpenChange={(open) => !open && setSelectedCaseStudy(null)}>
        <DialogContent className="max-w-4xl p-8 md:p-12">
          <DialogHeader className="sr-only">
            <DialogTitle>Case Study</DialogTitle>
            <DialogDescription>
              Read detailed case study
            </DialogDescription>
          </DialogHeader>
          {selectedCaseStudy && (
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src={selectedCaseStudy.logo} 
                  alt={selectedCaseStudy.companyName}
                  className="h-12 mb-6 object-contain object-left"
                />
                <blockquote className="text-2xl text-gray-800 font-medium mb-6 italic">
                  "{selectedCaseStudy.quote}"
                </blockquote>
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src={selectedCaseStudy.authorPhoto}
                    alt={selectedCaseStudy.authorName}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-bold text-gray-900">{selectedCaseStudy.authorName}</p>
                    <p className="text-gray-600">{selectedCaseStudy.authorTitle}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 mb-8">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold text-gray-900">{selectedCaseStudy.metric}</span>
                  </div>
                </div>
                <Button 
                  onClick={() => window.location.href = `/resources/case-studies/${selectedCaseStudy.slug}`}
                  className="group"
                >
                  Read Full Story
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
              <div className="aspect-[4/3] rounded-2xl shadow-xl overflow-hidden">
                <img 
                  src={selectedCaseStudy.image}
                  alt="Happy employees collaborating" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TrustLogosSection;
